import { Check, Plus, Warning, X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import {
  CARD_LINE_2_MAX_LENGTH,
  CreateDebitCardRequest,
  Entity,
} from '../../../../../../../types/apiTypes';
import { sanitizeCardLine2 } from '../../../../../../../utils/cardLine2';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { Divider, Radio, Select } from 'antd';
import { Truck } from '../../../../../../../types/dbTypes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddNewAddressModal from '../../../settings/widgets/AddNewAddressModal';

// Keep in sync with MAX_ACTIVE_CARDS_PER_TRUCK in tach-architecture createChildDebitCardForTruck.ts
const MAX_ACTIVE_CARDS_PER_TRUCK = 3;
// Keep in sync with MAX_ACTIVE_BUSINESS_CARDS_PER_CUSTOMER in tach-architecture createChildDebitCardForTruck.ts
const MAX_ACTIVE_BUSINESS_CARDS_PER_CUSTOMER = 5;

export const AddNewCardModal = ({
  isOpen,
  onClose,
  account_id,
  activeCardCountByTruckId,
  activeBusinessCardCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  account_id: string;
  activeCardCountByTruckId: Map<string, number>;
  activeBusinessCardCount: number;
}) => {
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const [newAddressPopupVisible, setNewAddressPopupVisible] = useState(false);
  const [loadingTrucks, setLoadingTrucks] = useState(false);
  const [cardType, setCardType] = useState<'truck' | 'business'>('truck');
  const [selectedTruck, setSelectedTruck] = useState('');
  const [cardLine2, setCardLine2] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const queryClient = useQueryClient();

  const isBusinessCard = cardType === 'business';
  const isAtBusinessCardCap =
    activeBusinessCardCount >= MAX_ACTIVE_BUSINESS_CARDS_PER_CUSTOMER;
  const selectedTruckCardCount =
    activeCardCountByTruckId.get(selectedTruck) ?? 0;
  const isAtCardCap = selectedTruckCardCount >= MAX_ACTIVE_CARDS_PER_TRUCK;
  const showMultipleCardWarning =
    selectedTruck && selectedTruckCardCount > 0 && !isAtCardCap;
  const allTrucksAtCap =
    trucks.length > 0 &&
    trucks.every(
      (truck) =>
        (activeCardCountByTruckId.get(truck.truck_id ?? '') ?? 0) >=
        MAX_ACTIVE_CARDS_PER_TRUCK
    );
  const fetchTrucks = async (customer_id: string) => {
    setLoadingTrucks(true);
    try {
      const response = await callProtectedApi(
        `/trucks/list/${customer_id}`,
        'get',
        {},
        'Error occurred while fetching trucks.'
      );
      const data = response?.data?.data?.trucks;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        setTrucks(data);
      } else {
        toast.error('Failed to fetch trucks.');
      }
    } catch (e: any) {
      console.error('Error occurred while fetching trucks: ', e);
    } finally {
      setLoadingTrucks(false);
    }
  };

  const { data: addresses = [], isLoading: loadingAddresses } = useQuery({
    queryKey: ['addresses', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/addresses/list/${userId ?? ''}`,
        'get',
        {},
        'Error occurred while fetching your addresses.'
      );
      return response?.data?.data?.addresses || [];
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const sortedAddresses = useMemo(() => {
    if (!addresses || addresses.length === 0) return [];
    return [...addresses].sort(
      (a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)
    );
  }, [addresses]);

  useEffect(() => {
    if (!sortedAddresses?.length) return;
    setSelectedAddress(sortedAddresses[0]?.address_id || '');
  }, [sortedAddresses]);

  const createCard = async (requestBody: CreateDebitCardRequest) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/debitCards`,
        'post',
        requestBody,
        'Error occurred while creating new card.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Successfully created new card.');
      } else {
        toast.error('Error occurred while creating new card.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating newly linked account record: ',
        e
      );
    } finally {
      queryClient.invalidateQueries({ queryKey: ['debitCards'] });
      onClose();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrucks(userId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (value: string) => {
    setSelectedTruck(value);
  };

  const handleAddNewAddress = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setAddressDropdownOpen(false);
    setNewAddressPopupVisible(true);
  };

  const formatAddress = useCallback((address: any) => {
    return `${address?.address_st_number ?? ''} ${address?.address_st_name ?? ''}${address?.address_unit && address?.address_unit.length ? ` ${address.address_unit},` : ''} ${address?.address_city ?? ''}, ${address?.address_state ?? ''} ${address?.address_zip ?? ''}`;
  }, []);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900 bg-opacity-50"
            onClick={loading ? undefined : onClose}
          >
            <div
              className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[90%] md:max-w-[60%] lg:max-w-[600px]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Add New Card
                </h2>
                <X
                  className={
                    loading
                      ? 'pointer-events-none opacity-40'
                      : 'hover:cursor-pointer hover:opacity-80'
                  }
                  onClick={loading ? undefined : onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {!loadingTrucks &&
              !loadingAddresses &&
              userRecord?.userType === Entity.Customer ? (
                <form className="flex h-full w-full flex-col items-center gap-3 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6">
                  <div className="h-[197px] w-[340px]">
                    <img
                      src="/bp-tach-card-no-data.svg"
                      alt="card-image"
                      className="h-[197px] w-[340px] rounded-lg object-cover"
                    />
                  </div>

                  <div className="w-full">
                    <label className="mb-1 mt-4 block text-sm font-medium text-graysecondary">
                      Card Type
                    </label>
                    <Radio.Group
                      value={cardType}
                      onChange={(e) => {
                        setCardType(e.target.value);
                        // Reset sibling state so a stale truck or label can't
                        // leak across card types.
                        setSelectedTruck('');
                        setCardLine2('');
                      }}
                      disabled={loading}
                      className="mb-2"
                    >
                      <Radio.Button value="truck">Truck Card</Radio.Button>
                      <Radio.Button value="business">
                        Business Card
                      </Radio.Button>
                    </Radio.Group>
                  </div>
                  {isBusinessCard ? (
                    <div className="w-full">
                      {isAtBusinessCardCap ? (
                        <p className="text-danger">
                          You already have{' '}
                          {MAX_ACTIVE_BUSINESS_CARDS_PER_CUSTOMER} business
                          cards, which is the maximum allowed. To add a new one,
                          please cancel an existing business card first.
                        </p>
                      ) : (
                        <>
                          <label className="mb-1 block text-sm font-medium text-graysecondary">
                            Card Label (optional)
                          </label>
                          <p className="mb-1 text-[12px] text-graysecondary">
                            Printed on the card under your business name.
                            Letters, numbers, and spaces only.
                          </p>
                          <div className="relative">
                            <input
                              type="text"
                              value={cardLine2}
                              disabled={loading}
                              onChange={(e) =>
                                setCardLine2(
                                  sanitizeCardLine2(e.target.value).slice(
                                    0,
                                    CARD_LINE_2_MAX_LENGTH
                                  )
                                )
                              }
                              placeholder="e.g. OPERATIONS"
                              className="h-[48px] w-full rounded-[4px] border-[1px] border-bordergray px-3 pr-14 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <span className="pointer-events-none absolute bottom-1.5 right-3 text-[11px] tabular-nums text-graysecondary">
                              {cardLine2.length}/{CARD_LINE_2_MAX_LENGTH}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="w-full">
                      <label className="mb-1 block text-sm font-medium text-graysecondary">
                        Asset
                      </label>
                      {allTrucksAtCap ? (
                        <p className="text-danger">
                          All of your trucks already have{' '}
                          {MAX_ACTIVE_CARDS_PER_TRUCK} TACH cards, which is the
                          maximum allowed per truck. To add a new card, cancel
                          an existing card first.
                        </p>
                      ) : trucks?.length > 0 ? (
                        <>
                          <Select
                            placeholder="Select Asset"
                            disabled={loading}
                            onChange={handleChange}
                            options={trucks?.map((truck) => ({
                              label:
                                truck?.truck_display_name +
                                ' ' +
                                truck?.vehicle_id_number,
                              value: truck?.truck_id,
                            }))}
                            className="h-[48px] w-full min-w-full"
                          />
                          {showMultipleCardWarning && (
                            <div className="mt-2 flex flex-row items-start gap-2 rounded-[8px] border-[1px] border-[#E2660C] bg-[#FCF0E7] px-3 py-2">
                              <Warning
                                size={20}
                                className="mt-0.5 flex-shrink-0 text-[#E2660C]"
                              />
                              <p className="text-[12px] text-primary">
                                This truck already has a TACH card. You can add
                                up to {MAX_ACTIVE_CARDS_PER_TRUCK} cards per
                                truck, but make sure to update your drivers'
                                truck assignments whenever drivers switch trucks
                                so your Driver Performance reports stay
                                accurate.
                              </p>
                            </div>
                          )}
                          {isAtCardCap && (
                            <p className="mt-2 text-danger">
                              This truck already has{' '}
                              {MAX_ACTIVE_CARDS_PER_TRUCK} TACH cards, which is
                              the maximum allowed. To add another card to this
                              truck, cancel an existing one first.
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-danger">
                          No trucks found. Please add a truck first.
                        </p>
                      )}
                    </div>
                  )}
                  {!(isBusinessCard && isAtBusinessCardCap) && (
                    <div className="w-full">
                      <label className="mb-1 mt-2 block text-sm font-medium text-graysecondary">
                        Address
                      </label>
                      <p className="mb-1 text-[12px] text-graysecondary">
                        This mailing address will also be the billing address
                        for the card.
                      </p>
                      {sortedAddresses?.length > 0 ? (
                        <Select
                          open={loading ? false : addressDropdownOpen}
                          disabled={loading}
                          onDropdownVisibleChange={setAddressDropdownOpen}
                          value={selectedAddress}
                          placeholder="Select Address"
                          onChange={setSelectedAddress}
                          options={sortedAddresses?.map((address) => ({
                            label: (
                              <div className="flex w-full flex-row items-center justify-between">
                                <span>{formatAddress(address)}</span>
                                {address.is_primary && (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <Check size={16} />
                                    <span className="text-[12px] font-semibold">
                                      Primary
                                    </span>
                                  </span>
                                )}
                              </div>
                            ),
                            value: address?.address_id,
                          }))}
                          className="h-[48px] w-full min-w-full"
                          dropdownRender={(menu) => (
                            <div>
                              {menu}
                              <Divider style={{ margin: '8px 0' }} />
                              <div className="px-3 pb-2">
                                <NeutralButton
                                  onClick={handleAddNewAddress}
                                  addClass="rounded-full w-full h-[48px] flex flex-row items-center justify-center !border-[#21252914]"
                                >
                                  <Plus size={16} />
                                  <span className="ml-3">Add New Address</span>
                                </NeutralButton>
                              </div>
                            </div>
                          )}
                        />
                      ) : (
                        <p className="text-danger">
                          No addresses found. Please add an address first.
                        </p>
                      )}
                    </div>
                  )}
                </form>
              ) : (
                <div className="flex h-full w-full items-center justify-center align-middle">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingTrucks}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                </div>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  disabled={loading}
                  addClass="rounded-full w-[238px] h-[48px] flex flex-row items-center justify-center"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={
                    loading ||
                    !selectedAddress ||
                    (isBusinessCard
                      ? isAtBusinessCardCap
                      : !selectedTruck ||
                        selectedTruck?.length === 0 ||
                        isAtCardCap)
                  }
                  onClick={async () =>
                    await createCard(
                      isBusinessCard
                        ? {
                            card_type: 'business',
                            customer_id: userId ?? '',
                            account_id,
                            address_id: selectedAddress,
                            ...(cardLine2 ? { card_line_2: cardLine2 } : {}),
                          }
                        : {
                            customer_id: userId ?? '',
                            truck_id: selectedTruck,
                            account_id,
                            address_id: selectedAddress,
                          }
                    )
                  }
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600 flex flex-row items-center justify-center"
                >
                  {!loading ? (
                    'Add Card'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
            {newAddressPopupVisible && (
              <AddNewAddressModal
                onClose={() => setNewAddressPopupVisible(false)}
                addresses={sortedAddresses}
                isOpen={newAddressPopupVisible}
              />
            )}
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default AddNewCardModal;
